import cn from "classnames";
import { ProgressBar } from "web-ui";

import type { BaseStatus } from "../api/languageChallenge";
import languages from "../data/languages.json";
import xpIcon from "../images/xp-bolt-active.png";
import xpWithSparkles from "../images/xp-bolt-with-sparkles.png";
import styles from "../styles/XpProgress.module.scss";
import { getEstimateXpPerDay } from "../util/challenges";
import type { LanguageId } from "../util/languages";

import { MegaFlag } from "./MegaFlag";

export interface Props {
  className?: string;
  status: BaseStatus;
}

const generateLanguageDescription = (
  learningLanguage: LanguageId,
  fromLanguage: LanguageId,
) => {
  if (learningLanguage === "mega") {
    return `${languages[learningLanguage].name} Challenge`;
  } else if (
    learningLanguage === "math" ||
    learningLanguage === "music" ||
    learningLanguage === "chess"
  ) {
    return languages[learningLanguage]?.name ?? "Unknown Language";
  } else {
    const learningLanguageName =
      languages[learningLanguage]?.name ?? "Unknown Language";
    const fromLanguageName =
      languages[fromLanguage]?.name ?? "Unknown Language";
    return `${learningLanguageName} from ${fromLanguageName}`;
  }
};

const CompleteXpProgress = ({ className, status }: Props) => (
  <div className={cn(className, styles["complete-xp-container"])}>
    <div className={styles["complete-xp-bolt"]}>
      <img alt="xp with sparkles" height="auto" src={xpWithSparkles} />
    </div>
    <div className={styles["complete-xp-description"]}>
      You completed the XP objective in the{" "}
      <MegaFlag className={styles["flag-big"]} lang={status.learningLanguage} />{" "}
      {generateLanguageDescription(
        status.learningLanguage,
        status.fromLanguage,
      )}{" "}
      course by earning {status.xpObjective.progress} XP during the challenge!
    </div>
  </div>
);

const IncompleteXpProgress = ({ className, status }: Props) => {
  const progressDenominator = status.xpObjective.goal;
  const progressNumerator = Math.min(
    status.xpObjective.progress,
    progressDenominator,
  );
  const estimateXpPerDay = getEstimateXpPerDay(
    status.dueTimeSecondsSinceEpoch * 1000,
    status.xpObjective.goal - status.xpObjective.progress,
  );
  return (
    <div className={cn(className, styles.wrap)}>
      <div className={styles["language-row"]}>
        <MegaFlag className={styles.flag} lang={status.learningLanguage} />
        <h3 className={styles["direction-text"]}>
          {generateLanguageDescription(
            status.learningLanguage,
            status.fromLanguage,
          )}
        </h3>
      </div>
      <ProgressBar
        className={styles["progress-bar"]}
        denominator={progressDenominator}
        height={20}
        numerator={progressNumerator}
      />
      <div className={styles["xp-container"]}>
        <div className={styles["xp-icon"]}>
          <img alt="xp icon" height={20} src={xpIcon} width={15} />
        </div>
        <div>
          <span className={styles["xp-numerator"]}>
            {status.xpObjective.progress}
          </span>
          {` / ${status.xpObjective.goal} XP`}
        </div>
      </div>
      <div className={styles["xp-text"]}>
        Earn <b>{estimateXpPerDay} XP per day</b> to complete the challenge
      </div>
    </div>
  );
};

const XpProgress = ({ className, status }: Props) =>
  status.xpObjective.isComplete ? (
    <CompleteXpProgress className={className} status={status} />
  ) : (
    <IncompleteXpProgress className={className} status={status} />
  );
export default XpProgress;
